import {
  Tolgee,
  DevTools,
  type TolgeeInstance,
  type TolgeeOptions,
  type TolgeeStaticData,
} from '@tolgee/web';
import { FormatIcu } from '@tolgee/format-icu';
import { TolgeeProvider } from '@tolgee/react';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales';
import { LocalePreference } from './locale-preference';

export type TolgeeSetupOptions = {
  apiKey?: string;
  apiUrl?: string;
  staticData?: TolgeeStaticData;
};

// `FormatIcu`, not `FormatSimple`. Tolgee's simple formatter substitutes `{param}`
// and nothing else, which is why every counted string in the corpus had exactly one
// form and the submissions editor printed "1 rânduri". ICU MessageFormat is the
// mechanism Tolgee offers for plurals, and it is the only one that can be right
// here: the three languages do not have the same number of forms. English has two
// (one/other), Romanian three (one = 1, few = 0 and 2..19, other = 20 and up, which
// is the form that takes "de": "1 rând", "3 rânduri", "21 de rânduri"), Russian
// three plus a fractional fallback (one = 1/21/31, few = 2..4, many = 0 and 5..20:
// "1 строка", "2 строки", "5 строк"). ICU picks the branch from CLDR's own rules for
// the active language, so each locale file states its own set of forms and no
// helper beside the library has to know any of this.
//
// Two consequences worth knowing. A message whose parameter the call site does not
// supply renders as the literal "invalid" rather than leaving the placeholder
// visible; `tools/scripts/guard-plurals.mjs` keeps the placeholder sets identical
// across the three locales so a translation cannot ask for a parameter its siblings
// do not. And a lone `'` only escapes when it precedes `{`, `}` or `#`, so the
// corpus's English apostrophes ("Today's action") are untouched.
export function createTolgee(options: TolgeeSetupOptions): TolgeeInstance {
  // `defaultLanguage`, never `language`: `language` pins the current language in
  // the state before `run()`, and the core then returns from `initializeLanguage`
  // without ever asking the storage — the preference would be written and never
  // read (caiet ACCS-03). `defaultLanguage` is the value used only when nothing
  // was stored, i.e. on a first visit.
  const init: TolgeeOptions = {
    defaultLanguage: DEFAULT_LOCALE,
    availableLanguages: [...SUPPORTED_LOCALES],
    fallbackLanguage: 'en',
  };
  if (options.apiKey) init.apiKey = options.apiKey;
  if (options.apiUrl) init.apiUrl = options.apiUrl;
  if (options.staticData) init.staticData = options.staticData;
  // DevTools' in-context observer wraps every translation in invisible marker
  // characters so the Tolgee editor can map DOM text back to keys. That only
  // makes sense when actually editing against the cloud (apiKey set); otherwise
  // it just pollutes the DOM (breaks exact text matching, screen readers).
  const instance = options.apiKey
    ? Tolgee().use(DevTools()).use(FormatIcu()).use(LocalePreference())
    : Tolgee().use(FormatIcu()).use(LocalePreference());
  return instance.init(init);
}

export { TolgeeProvider };
